import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { reportsRepositoryService, summaryReportsService } from '../services';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { 
  Search, 
  Download, 
  FileText, 
  Calendar, 
  Building2, 
  User, 
  Filter,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Loader2,
  AlertCircle,
  X,
  History,
  CheckCircle2,
  Clock,
  FileWarning,
  Archive,
  ArchiveRestore,
  Trash2,
  Settings,
  UtensilsCrossed,
  Wine,
  Eye
} from 'lucide-react';

/**
 * Reports Repository Page - Phase 2.6A
 * Internal search and management interface for all reports across jobs.
 */
const ReportsRepositoryPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isAdmin, canManageSites } = useAuth();
  const { toast } = useToast();
  
  // Permission checks
  const canArchive = isAdmin || canManageSites; // Admin/Scheduler can archive
  const canRestore = isAdmin; // Only Admin can restore
  const canDeleteAP = isAdmin || user?.role === 'senior_scheduler' || (user?.roles || []).includes('senior_scheduler'); // Admin/Senior Scheduler (auditors delete from the AP page itself)
  const canHardDelete = isAdmin; // Only Admin can hard delete
  
  // Get current month date range
  // Format date as YYYY-MM-DD using LOCAL timezone (not UTC)
  const formatLocalDate = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const getDefaultDateRange = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      from: formatLocalDate(firstDay),
      to: formatLocalDate(lastDay)
    };
  };
  
  const defaultDates = getDefaultDateRange();
  
  // Filter state - defaults to current month
  const [filters, setFilters] = useState({
    date_from: defaultDates.from,
    date_to: defaultDates.to,
    date_field: 'scheduled_date',
    group_ids: [],
    include_independents: null,
    site_ids: [],
    auditor_user_ids: [],
    uploaded_by_user_ids: [],
    service_types: [],
    report_types: [],
    statuses: [],
    search_text: '',
    sort: 'date_desc',
    show_archived: false // New filter for archived reports
  });
  
  // Filter options
  const [filterOptions, setFilterOptions] = useState({
    groups: [],
    sites: [],
    auditors: [],
    uploaders: []
  });
  
  // Results
  const [results, setResults] = useState({ total: 0, items: [] });
  const [page, setPage] = useState(0);
  const pageSize = 50;
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [loadingFilters, setLoadingFilters] = useState(true);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  
  // Version modal
  const [versionModal, setVersionModal] = useState({ open: false, report: null, versions: [] });
  const [loadingVersions, setLoadingVersions] = useState(false);
  
  // Archive dialog state
  const [archiveDialog, setArchiveDialog] = useState({ open: false, version: null });
  const [archiveReason, setArchiveReason] = useState('');
  const [archiving, setArchiving] = useState(false);
  
  // Restore dialog state
  const [restoreDialog, setRestoreDialog] = useState({ open: false, version: null });
  const [restoring, setRestoring] = useState(false);
  
  // Hard delete dialog state (one-time cleanup)
  const [deleteDialog, setDeleteDialog] = useState({ open: false, version: null });
  const [deleting, setDeleting] = useState(false);
  
  // Delete job dialog state (Admin only - complete job removal)
  const [deleteJobDialog, setDeleteJobDialog] = useState({ open: false, job: null });
  const [deletingJob, setDeletingJob] = useState(false);

  // AP delete state (Admin/Senior Scheduler)
  const [apDeleteDialog, setApDeleteDialog] = useState({ open: false, ap: null, reason: '' });
  const [deletingAp, setDeletingAp] = useState(false);
  
  // Repository category state - unified view
  const [activeTab, setActiveTabState] = useState(() => {
    return searchParams.get('tab') || 'all';
  });
  
  const setActiveTab = useCallback((tab) => {
    setActiveTabState(tab);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (tab === 'all') {
        next.delete('tab');
      } else {
        next.set('tab', tab);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  
  // Summary Stock Audit Reports state - Phase 2A
  const [summaryReports, setSummaryReports] = useState({ total: 0, items: [] });
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(null);
  const [summaryFilters, setSummaryFilters] = useState({
    status: '',
    scope: 'BEVERAGE', // Default to Beverage as per user requirement
    date_from: defaultDates.from,
    date_to: defaultDates.to,
    search: '',
    has_ap: false,  // AP8: "Has AP" quick-filter toggle
  });

  // AP8: Action Plans tab state
  const [apList, setApList] = useState([]);
  const [apTotal, setApTotal] = useState(0);
  const [apListLoading, setApListLoading] = useState(false);
  const [apListError, setApListError] = useState(null);
  // AP8 search/filter state
  const [apSearchText,   setApSearchText]   = useState('');
  const [apSearchStatus, setApSearchStatus] = useState('');
  const [apSearchScope,  setApSearchScope]  = useState('');
  const [apPdfLoading,   setApPdfLoading]   = useState({});

  const loadApList = useCallback(async (appendOffset = 0) => {
    setApListLoading(true);
    if (appendOffset === 0) setApListError(null);
    try {
      const token = localStorage.getItem('access_token') || '';
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/action-plans?limit=200&offset=${appendOffset}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      // Support both paginated envelope {items, total} and legacy flat array
      const newItems = data.items || (Array.isArray(data) ? data : []);
      const total = data.total ?? newItems.length;
      setApList(prev => appendOffset === 0 ? newItems : [...prev, ...newItems]);
      setApTotal(total);
    } catch (err) {
      setApListError('Failed to load Action Plans');
    } finally {
      setApListLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'action-plans') loadApList();
  }, [activeTab, loadApList]);

  // Load Summary Reports
  const loadSummaryReports = useCallback(async () => {
    setSummaryLoading(true);
    setSummaryError(null);
    try {
      const params = {
        limit: 50,
        offset: 0
      };
      if (summaryFilters.status) params.status = summaryFilters.status;
      if (summaryFilters.scope) params.scope = summaryFilters.scope;
      if (summaryFilters.date_from) params.date_from = summaryFilters.date_from;
      if (summaryFilters.date_to) params.date_to = summaryFilters.date_to;
      if (summaryFilters.search) params.search = summaryFilters.search;
      if (summaryFilters.has_ap) params.has_ap = true;
      
      const data = await summaryReportsService.list(params);
      // Ensure valid response structure
      setSummaryReports({
        total: data?.total ?? 0,
        items: data?.items ?? []
      });
    } catch (err) {
      console.error('Failed to load summary reports:', err);
      // More specific error message based on error type
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 404) {
        setSummaryError('Summary Reports endpoint not found (404)');
      } else if (status === 500) {
        setSummaryError(`Server error: ${detail || 'Internal server error'}`);
      } else if (status === 401 || status === 403) {
        setSummaryError('Access denied - please log in again');
      } else if (err.message?.includes('Network')) {
        setSummaryError('Network error - please check your connection');
      } else {
        setSummaryError(detail || 'Failed to load Summary Stock Audit Reports');
      }
      // Set empty state on error to avoid stale data
      setSummaryReports({ total: 0, items: [] });
    } finally {
      setSummaryLoading(false);
    }
  }, [summaryFilters]);
  
  // Load summary reports when category includes them
  useEffect(() => {
    if (activeTab === 'summary-reports' || activeTab === 'all') {
      loadSummaryReports();
    }
  }, [activeTab, loadSummaryReports]);

  // Load filter options on mount
  useEffect(() => {
    const loadFilters = async () => {
      try {
        const data = await reportsRepositoryService.getFilters();
        setFilterOptions(data);
      } catch (err) {
        console.error('Failed to load filters:', err);
      } finally {
        setLoadingFilters(false);
      }
    };
    loadFilters();
  }, []);

  // Search function – accepts explicit page to avoid stale-state bugs
  const handleSearch = useCallback(async (resetPage = true, explicitPage) => {
    setLoading(true);
    setError(null);
    
    const activePage = resetPage ? 0 : (explicitPage !== undefined ? explicitPage : page);
    const offset = activePage * pageSize;
    if (resetPage) setPage(0);
    
    try {
      const searchParams = {
        ...filters,
        limit: pageSize,
        offset,
        show_archived: filters.show_archived, // Include archived flag
        // Convert empty arrays to undefined
        group_ids: filters.group_ids.length > 0 ? filters.group_ids : undefined,
        site_ids: filters.site_ids.length > 0 ? filters.site_ids : undefined,
        auditor_user_ids: filters.auditor_user_ids.length > 0 ? filters.auditor_user_ids : undefined,
        uploaded_by_user_ids: filters.uploaded_by_user_ids.length > 0 ? filters.uploaded_by_user_ids : undefined,
        service_types: filters.service_types.length > 0 ? filters.service_types : undefined,
        report_types: filters.report_types.length > 0 ? filters.report_types : undefined,
        statuses: filters.statuses.length > 0 ? filters.statuses : undefined,
        date_from: filters.date_from || undefined,
        date_to: filters.date_to || undefined,
        search_text: filters.search_text || undefined,
        include_independents: filters.include_independents
      };
      
      const data = await reportsRepositoryService.search(searchParams);
      setResults(data);
    } catch (err) {
      setError('Failed to search reports. Please try again.');
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  // Initial search on mount
  useEffect(() => {
    handleSearch();
  }, []);

  // Handle page change – pass explicit page number to avoid async state issue
  const handlePageChange = (newPage) => {
    setPage(newPage);
    handleSearch(false, newPage);
  };

  // Clear filters - reset to current month
  const handleClearFilters = () => {
    const freshDates = getDefaultDateRange();
    setFilters({
      date_from: freshDates.from,
      date_to: freshDates.to,
      date_field: 'scheduled_date',
      group_ids: [],
      include_independents: null,
      site_ids: [],
      auditor_user_ids: [],
      uploaded_by_user_ids: [],
      service_types: [],
      report_types: [],
      statuses: [],
      search_text: '',
      sort: 'date_desc',
      show_archived: false
    });
  };

  // Handle download
  const handleDownload = async (versionId, filename) => {
    setDownloadingId(versionId);
    try {
      await reportsRepositoryService.downloadVersion(versionId, filename);
    } catch (err) {
      setError(err.message);
    } finally {
      setDownloadingId(null);
    }
  };

  // Handle view versions
  const handleViewVersions = async (report) => {
    setLoadingVersions(true);
    setVersionModal({ open: true, report, versions: [] });
    
    try {
      const data = await reportsRepositoryService.getVersions(report.report_id, filters.show_archived);
      setVersionModal(prev => ({ ...prev, versions: data.versions }));
    } catch (err) {
      setError('Failed to load versions');
    } finally {
      setLoadingVersions(false);
    }
  };
  
  // Handle archive report version
  const handleArchive = async () => {
    if (!archiveDialog.version || !archiveReason.trim()) return;
    
    setArchiving(true);
    try {
      await reportsRepositoryService.archiveVersion(archiveDialog.version.report_version_id, archiveReason);
      toast({
        title: 'Report Archived',
        description: `Version ${archiveDialog.version.version_number} has been archived.`,
      });
      setArchiveDialog({ open: false, version: null });
      setArchiveReason('');
      // Refresh the search results
      handleSearch(false);
      // If version modal is open, refresh it
      if (versionModal.open && versionModal.report) {
        handleViewVersions(versionModal.report);
      }
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Archive Failed',
        description: err.message || 'Failed to archive report',
      });
    } finally {
      setArchiving(false);
    }
  };
  
  // Handle restore archived report version
  const handleRestore = async () => {
    if (!restoreDialog.version) return;
    
    setRestoring(true);
    try {
      // Use version_id which is the field set by the version modal
      const versionId = restoreDialog.version.version_id || restoreDialog.version.report_version_id;
      await reportsRepositoryService.restoreVersion(versionId);
      toast({
        title: 'Report Restored',
        description: `Version ${restoreDialog.version.version_number} has been restored.`,
      });
      setRestoreDialog({ open: false, version: null });
      // Refresh the search results
      handleSearch(false);
      // If version modal is open, refresh it
      if (versionModal.open && versionModal.report) {
        handleViewVersions(versionModal.report);
      }
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Restore Failed',
        description: err.message || 'Failed to restore report',
      });
    } finally {
      setRestoring(false);
    }
  };
  
  // Handle hard delete (one-time cleanup)
  const handleHardDelete = async () => {
    if (!deleteDialog.version) return;
    
    setDeleting(true);
    try {
      // Use version_id which is the field set by the version modal
      const versionId = deleteDialog.version.version_id || deleteDialog.version.report_version_id;
      await reportsRepositoryService.hardDeleteVersion(versionId);
      toast({
        title: 'Report Permanently Deleted',
        description: `Version ${deleteDialog.version.version_number} has been permanently removed.`,
      });
      setDeleteDialog({ open: false, version: null });
      // Refresh the search results
      handleSearch(false);
      // Close version modal since the version is gone
      setVersionModal({ open: false, report: null, versions: [] });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Delete Failed',
        description: err.message || 'Failed to delete report',
      });
    } finally {
      setDeleting(false);
    }
  };

  // Handle job reports deletion (Admin only - removes reports but keeps job in schedule)
  const handleDeleteJobReports = async () => {
    if (!deleteJobDialog.job) return;
    
    setDeletingJob(true);
    try {
      const result = await reportsRepositoryService.deleteAllJobReports(deleteJobDialog.job.job_id);
      toast({
        title: 'Reports Permanently Deleted',
        description: result.message || 'All reports have been removed. Job remains in schedule.',
      });
      setDeleteJobDialog({ open: false, job: null });
      // Refresh the search results
      handleSearch(false);
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Delete Failed',
        description: err.response?.data?.detail || err.message || 'Failed to delete reports',
      });
    } finally {
      setDeletingJob(false);
    }
  };

  // Handle AP delete (Admin / Senior Scheduler via clean-delete endpoint)
  const handleDeleteAP = async () => {
    const ap = apDeleteDialog.ap;
    if (!ap) return;
    setDeletingAp(true);
    try {
      const token = localStorage.getItem('access_token') || '';
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/action-plans/${ap.id}/delete-clean`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: apDeleteDialog.reason || 'Admin clean-up via archive' }),
        }
      );
      const body = await r.json();

      if (r.status === 409) {
        // Plan has client-authored content — fall back to mark-dummy + purge for Admins
        if (!isAdmin) {
          toast({ variant: 'destructive', title: 'Cannot Delete', description: body.detail || 'Plan has saved content — only Admins can force-delete.' });
          return;
        }
        // Admin: mark as dummy then purge
        const r2 = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/action-plans/${ap.id}/mark-dummy`,
          { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
        );
        if (!r2.ok) {
          const b2 = await r2.json();
          toast({ variant: 'destructive', title: 'Mark-dummy Failed', description: b2.detail || 'Could not mark plan as dummy.' });
          return;
        }
        const r3 = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/action-plans/purge-dummy`,
          {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({
            ap_ids: [ap.id],
            confirmation: 'PURGE DUMMY APS',
            reason: apDeleteDialog.reason || 'Admin archive deletion via Reports Repository',
          }),
          }
        );
        if (!r3.ok) {
          const b3 = await r3.json();
          toast({ variant: 'destructive', title: 'Purge Failed', description: b3.detail || 'Could not purge plan.' });
          return;
        }
        toast({ title: 'Action Plan Purged', description: `AP (id=${ap.id}) marked and purged successfully.` });
      } else if (!r.ok) {
        // body.detail may be a Pydantic array ([{msg, loc, ...}]) or a plain string
        const rawDetail = body?.detail;
        const errMsg = typeof rawDetail === 'string'
          ? rawDetail
          : Array.isArray(rawDetail)
            ? rawDetail.map(d => d.msg || JSON.stringify(d)).join('; ')
            : 'Could not delete Action Plan.';
        toast({ variant: 'destructive', title: 'Delete Failed', description: errMsg });
        return;
      } else {
        toast({ title: 'Action Plan Deleted', description: `AP for ${ap.site_name || ap.id} has been removed.` });
      }

      setApDeleteDialog({ open: false, ap: null, reason: '' });
      // Refresh the AP list from the start (offset=0) to reflect the deletion
      const token2 = localStorage.getItem('access_token') || '';
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/action-plans?limit=200&offset=0`, { headers: { Authorization: `Bearer ${token2}` } });
      const data = await resp.json();
      const refreshed = data.items || (Array.isArray(data) ? data : []);
      setApList(refreshed);
      setApTotal(data.total ?? refreshed.length);
    } catch (err) {
      toast({ variant: 'destructive', title: 'Delete Failed', description: err.message || 'An error occurred.' });
    } finally {
      setDeletingAp(false);
    }
  };

  // Multi-select toggle helper
  const toggleMultiSelect = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: prev[key].includes(value)
        ? prev[key].filter(v => v !== value)
        : [...prev[key], value]
    }));
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'PUBLISHED': return 'bg-emerald-100 text-emerald-800';
      case 'PENDING_APPROVAL': return 'bg-amber-100 text-amber-800';
      case 'UNPUBLISHED': return 'bg-slate-100 text-slate-800';
      case 'DRAFT': return 'bg-blue-100 text-blue-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'PUBLISHED': return <CheckCircle2 className="h-3 w-3" />;
      case 'PENDING_APPROVAL': return <Clock className="h-3 w-3" />;
      case 'UNPUBLISHED': return <FileWarning className="h-3 w-3" />;
      default: return null;
    }
  };

  return (
    <div className="space-y-6" data-testid="reports-repository-page">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Audit Reports</h1>
          <p className="text-sm text-slate-500 mt-1">
            Search and manage reports across all jobs
          </p>
        </div>
      </div>
      
      {/* Repository Tabs - Simplified with Summary Reports as primary */}
      
      {/* Category Toggle */}
      <div className="flex items-center gap-2 mb-2" data-testid="report-category-toggle">
        <span className="text-sm font-medium text-slate-600">Show:</span>
        <div className="flex border rounded-lg overflow-hidden">
          <button
            onClick={() => setActiveTab('all')}
            className={`px-3 py-1.5 text-xs font-medium transition-colors ${
              activeTab === 'all' ? 'bg-slate-700 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
            data-testid="tab-all"
          >
            All Reports
          </button>
          <button
            onClick={() => setActiveTab('summary-reports')}
            className={`px-3 py-1.5 text-xs font-medium transition-colors border-l ${
              activeTab === 'summary-reports' ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
            data-testid="tab-summary-reports"
          >
            <span className="flex items-center gap-1"><FileText className="h-3 w-3" /> Summary Audit Reports</span>
          </button>
          <button
            onClick={() => setActiveTab('client-reports')}
            className={`px-3 py-1.5 text-xs font-medium transition-colors border-l ${
              activeTab === 'client-reports' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
            data-testid="tab-client-reports"
          >
            <span className="flex items-center gap-1"><Archive className="h-3 w-3" /> Attachments & Evidence</span>
          </button>
          <button
            onClick={() => setActiveTab('action-plans')}
            className={`px-3 py-1.5 text-xs font-medium transition-colors border-l ${
              activeTab === 'action-plans' ? 'bg-amber-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
            data-testid="tab-action-plans"
          >
            <span className="flex items-center gap-1"><FileWarning className="h-3 w-3" /> Action Plans</span>
          </button>
        </div>
      </div>
      
      <div className="space-y-6">
        {/* Attachments & Evidence Section */}
        {(activeTab === 'all' || activeTab === 'client-reports') && (
          <div className="space-y-4">
            {activeTab === 'all' && (
              <h2 className="text-base font-semibold text-slate-800 flex items-center gap-2">
                <Archive className="h-4 w-4" /> Attachments & Evidence
              </h2>
            )}
          {/* Filter toggle button */}
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="h-4 w-4 mr-2" />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
              {showFilters ? <ChevronUp className="h-4 w-4 ml-2" /> : <ChevronDown className="h-4 w-4 ml-2" />}
            </Button>
          </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <span className="text-sm text-red-700">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-600 hover:text-red-800">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Filters Panel */}
      {showFilters && (
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Date Range */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Date Range</label>
                <div className="flex gap-2">
                  <Input
                    type="date"
                    value={filters.date_from}
                    max={filters.date_to || undefined}
                    onChange={(e) => setFilters(prev => {
                      // iter452 — auto-drag the other end so the range never
                      // inverts. Previously users could set date_to < date_from
                      // and the search silently returned 0 rows, giving the
                      // impression that "the second date isn't working".
                      const newFrom = e.target.value;
                      const shouldClampTo = newFrom && prev.date_to && newFrom > prev.date_to;
                      return { ...prev, date_from: newFrom, date_to: shouldClampTo ? newFrom : prev.date_to };
                    })}
                    className="flex-1"
                    data-testid="date-from"
                  />
                  <Input
                    type="date"
                    value={filters.date_to}
                    min={filters.date_from || undefined}
                    onChange={(e) => setFilters(prev => {
                      const newTo = e.target.value;
                      const shouldClampFrom = newTo && prev.date_from && newTo < prev.date_from;
                      return { ...prev, date_to: newTo, date_from: shouldClampFrom ? newTo : prev.date_from };
                    })}
                    className="flex-1"
                    data-testid="date-to"
                  />
                </div>
                <div className="flex gap-1.5 flex-wrap">
                  {[
                    { label: 'Last Month', offset: -1 },
                    { label: 'This Month', offset: 0 },
                    { label: 'Next Month', offset: 1 },
                  ].map(({ label, offset }) => {
                    const d = new Date();
                    const from = new Date(d.getFullYear(), d.getMonth() + offset, 1);
                    const to = new Date(d.getFullYear(), d.getMonth() + offset + 1, 0);
                    return (
                      <Button
                        key={label}
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-6 text-[11px] px-2"
                        onClick={() => setFilters(prev => ({
                          ...prev,
                          date_from: formatLocalDate(from),
                          date_to: formatLocalDate(to),
                        }))}
                        data-testid={`date-shortcut-${label.toLowerCase().replace(' ', '-')}`}
                      >
                        {label}
                      </Button>
                    );
                  })}
                </div>
              </div>

              {/* Date Field Selector */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Date Field</label>
                <Select
                  value={filters.date_field}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, date_field: value }))}
                >
                  <SelectTrigger data-testid="date-field-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="scheduled_date">Scheduled Date</SelectItem>
                    <SelectItem value="uploaded_at">Upload Date</SelectItem>
                    <SelectItem value="completion_date">Completion Date</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Group Filter */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Group</label>
                <Select
                  value={filters.group_ids.length === 1 ? String(filters.group_ids[0]) : '__all__'}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, group_ids: value === '__all__' ? [] : [parseInt(value)] }))}
                >
                  <SelectTrigger data-testid="group-select">
                    <SelectValue placeholder="All groups" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All groups</SelectItem>
                    {filterOptions.groups.map(g => (
                      <SelectItem key={g.id} value={String(g.id)}>{g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Include Independents */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Client Type</label>
                <Select
                  value={filters.include_independents === null ? 'all' : filters.include_independents ? 'independents' : 'groups'}
                  onValueChange={(value) => setFilters(prev => ({ 
                    ...prev, 
                    include_independents: value === 'all' ? null : value === 'independents' 
                  }))}
                >
                  <SelectTrigger data-testid="client-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Clients</SelectItem>
                    <SelectItem value="groups">Groups Only</SelectItem>
                    <SelectItem value="independents">Independents Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Site Filter */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Site</label>
                <Select
                  value={filters.site_ids.length === 1 ? String(filters.site_ids[0]) : '__all__'}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, site_ids: value === '__all__' ? [] : [parseInt(value)] }))}
                >
                  <SelectTrigger data-testid="site-select">
                    <SelectValue placeholder="All sites" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All sites</SelectItem>
                    {filterOptions.sites.map(s => (
                      <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Auditor Filter */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Auditor</label>
                <Select
                  value={filters.auditor_user_ids.length === 1 ? String(filters.auditor_user_ids[0]) : '__all__'}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, auditor_user_ids: value === '__all__' ? [] : [parseInt(value)] }))}
                >
                  <SelectTrigger data-testid="auditor-select">
                    <SelectValue placeholder="All auditors" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All auditors</SelectItem>
                    {filterOptions.auditors.map(a => (
                      <SelectItem key={a.id} value={String(a.id)}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Status Filter */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Status</label>
                <Select
                  value={filters.statuses.length === 1 ? filters.statuses[0] : '__all__'}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, statuses: value === '__all__' ? [] : [value] }))}
                >
                  <SelectTrigger data-testid="status-select">
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All statuses</SelectItem>
                    <SelectItem value="PUBLISHED">Published</SelectItem>
                    <SelectItem value="PENDING_APPROVAL">Pending Approval</SelectItem>
                    <SelectItem value="UNPUBLISHED">Unpublished</SelectItem>
                    <SelectItem value="DRAFT">Draft</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Report Type Filter */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Report Type</label>
                <Select
                  value={filters.report_types.length === 1 ? filters.report_types[0] : '__all__'}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, report_types: value === '__all__' ? [] : [value] }))}
                >
                  <SelectTrigger data-testid="report-type-select">
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All types</SelectItem>
                    <SelectItem value="STOCK">Stock Report</SelectItem>
                    <SelectItem value="AUDIT">Audit Report</SelectItem>
                    <SelectItem value="PS">PS File</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Service Type Filter */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Service</label>
                <Select
                  value={filters.service_types.length === 1 ? filters.service_types[0] : '__all__'}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, service_types: value === '__all__' ? [] : [value] }))}
                >
                  <SelectTrigger data-testid="service-type-select">
                    <SelectValue placeholder="All services" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All services</SelectItem>
                    <SelectItem value="FOOD">Food</SelectItem>
                    <SelectItem value="BEVERAGE">Beverage</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Free Text Search */}
              <div className="space-y-2 md:col-span-2">
                <label className="text-sm font-medium text-slate-700">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search site name, job reference..."
                    value={filters.search_text}
                    onChange={(e) => setFilters(prev => ({ ...prev, search_text: e.target.value }))}
                    className="pl-10"
                    data-testid="search-input"
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  />
                </div>
              </div>

              {/* Sort */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Sort</label>
                <Select
                  value={filters.sort}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, sort: value }))}
                >
                  <SelectTrigger data-testid="sort-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="date_desc">Newest First</SelectItem>
                    <SelectItem value="date_asc">Oldest First</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-between mt-6 pt-4 border-t">
              <div className="flex gap-3">
                <Button 
                  onClick={() => handleSearch()} 
                  disabled={loading}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="search-btn"
                >
                  {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                  Search
                </Button>
                <Button variant="outline" onClick={handleClearFilters} data-testid="clear-btn">
                  Clear Filters
                </Button>
              </div>
              
              {/* Show Archived Toggle (Admin/Scheduler only) */}
              {canArchive && (
                <div className="flex items-center gap-2">
                  <Switch
                    id="show-archived"
                    checked={filters.show_archived}
                    onCheckedChange={(checked) => setFilters(prev => ({ ...prev, show_archived: checked }))}
                    data-testid="show-archived-toggle"
                  />
                  <Label htmlFor="show-archived" className="text-sm text-slate-600 cursor-pointer">
                    <Archive className="h-4 w-4 inline mr-1" />
                    Show Archived
                  </Label>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results Summary */}
      <div className="flex items-center justify-between text-sm text-slate-600" data-testid="results-summary">
        <span>
          {loading ? 'Searching...' : `${results.total} jobs with reports found`}
        </span>
        {results.total > pageSize && (
          <span>
            Showing {page * pageSize + 1}-{Math.min((page + 1) * pageSize, results.total)} &middot; Page {page + 1} of {Math.ceil(results.total / pageSize)}
          </span>
        )}
      </div>

      {/* Results Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
            </div>
          ) : results.items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-500">
              <FileText className="h-12 w-12 text-slate-300 mb-3" />
              <p>No reports found matching your criteria</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Site / Group</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Auditors</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Reports</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {results.items.map((item) => (
                    <tr key={item.job_id} className="hover:bg-slate-50" data-testid={`job-row-${item.job_id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-slate-400" />
                          <span className="font-medium">{formatDate(item.scheduled_date)}</span>
                        </div>
                        {item.completion_date && (
                          <div className="text-xs text-slate-500 mt-1">
                            Completed: {formatDate(item.completion_date)}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-slate-400" />
                          <span className="font-medium">{item.site_name}</span>
                        </div>
                        {item.group_name && (
                          <div className="text-xs text-slate-500 mt-1">{item.group_name}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {item.auditor_names.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {item.auditor_names.map((name, i) => (
                              <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-slate-100 text-slate-700">
                                <User className="h-3 w-3 mr-1" />
                                {name}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-slate-400 text-sm">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="space-y-2">
                          {item.reports.map((report) => (
                            <div key={report.report_id} className="flex items-center gap-2 text-sm">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${getStatusColor(report.status)}`}>
                                {getStatusIcon(report.status)}
                                {report.status}
                              </span>
                              <span className="text-slate-600">{report.service_type} / {report.report_type}</span>
                              {report.is_internal_only && (
                                <span className="text-xs text-amber-600">(Internal)</span>
                              )}
                              {report.latest_version && (
                                <div className="flex items-center gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 px-2 text-emerald-600 hover:text-emerald-700"
                                    onClick={() => handleDownload(report.latest_version.report_version_id, report.latest_version.filename)}
                                    disabled={downloadingId === report.latest_version.report_version_id}
                                    data-testid={`download-btn-${report.report_id}`}
                                    title="Download latest version"
                                  >
                                    {downloadingId === report.latest_version.report_version_id ? (
                                      <Loader2 className="h-3 w-3 animate-spin" />
                                    ) : (
                                      <Download className="h-3 w-3" />
                                    )}
                                  </Button>
                                  {/* Always show manage button (archive/history) - not just for multiple versions */}
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 px-2 text-slate-600 hover:text-slate-700"
                                    onClick={() => handleViewVersions(report)}
                                    data-testid={`versions-btn-${report.report_id}`}
                                    title={canArchive || canHardDelete ? "View versions & manage" : "View version history"}
                                  >
                                    {report.version_count > 1 ? (
                                      <>
                                        <History className="h-3 w-3" />
                                        <span className="text-xs ml-1">{report.version_count}</span>
                                      </>
                                    ) : (
                                      <Settings className="h-3 w-3" />
                                    )}
                                  </Button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-xs" 
                            data-testid={`view-job-btn-${item.job_id}`}
                            onClick={() => navigate(`/jobs/${item.job_id}`)}
                          >
                            <ExternalLink className="h-3 w-3 mr-1" />
                            View Job
                          </Button>
                          {isAdmin && (
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50" 
                              data-testid={`delete-job-btn-${item.job_id}`}
                              onClick={() => setDeleteJobDialog({ open: true, job: item })}
                            >
                              <Trash2 className="h-3 w-3 mr-1" />
                              Delete
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {results.total > pageSize && (
        <div className="flex items-center justify-between gap-2 mt-2" data-testid="audit-pagination">
          <span className="text-sm text-slate-500">
            Showing {page * pageSize + 1}-{Math.min((page + 1) * pageSize, results.total)} of {results.total} jobs
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 0}
              onClick={() => handlePageChange(page - 1)}
              data-testid="audit-prev-page"
            >
              Previous
            </Button>
            <span className="text-sm text-slate-600">
              Page {page + 1} of {Math.ceil(results.total / pageSize)}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={(page + 1) * pageSize >= results.total}
              onClick={() => handlePageChange(page + 1)}
              data-testid="audit-next-page"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Versions Modal */}
      <Dialog open={versionModal.open} onOpenChange={(open) => setVersionModal(prev => ({ ...prev, open }))}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Report Versions & Actions
              {versionModal.report && (
                <span className="text-sm font-normal text-slate-500 ml-2">
                  {versionModal.report.service_type} / {versionModal.report.report_type}
                </span>
              )}
            </DialogTitle>
            <DialogDescription>
              Download, archive, or delete report versions. 
              {canArchive && " Schedulers can archive. "}
              {canHardDelete && " Admins can delete. "}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {loadingVersions ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
              </div>
            ) : versionModal.versions.length === 0 ? (
              <p className="text-slate-500 text-center py-4">No versions found</p>
            ) : (
              versionModal.versions.map((version) => (
                <div 
                  key={version.version_id} 
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    version.is_archived ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <FileText className="h-4 w-4 text-slate-400 flex-shrink-0" />
                      <span className="font-medium">Version {version.version_number}</span>
                      {version.is_archived && (
                        <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300 text-xs">
                          <Archive className="h-3 w-3 mr-1" />
                          Archived
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-1 truncate">
                      {version.filename}
                      {version.file_size_bytes && (
                        <span className="ml-2">({(version.file_size_bytes / 1024).toFixed(1)} KB)</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500">
                      Uploaded {formatDate(version.uploaded_at)} by {version.uploaded_by || 'Unknown'}
                    </div>
                    {version.notes && (
                      <div className="text-xs text-slate-600 mt-1 italic">{version.notes}</div>
                    )}
                    {version.is_archived && version.archive_reason && (
                      <div className="text-xs text-amber-700 mt-1">
                        <strong>Archive reason:</strong> {version.archive_reason}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                    {/* Download Button */}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownload(version.version_id, version.filename)}
                      disabled={downloadingId === version.version_id}
                      data-testid={`version-download-${version.version_id}`}
                    >
                      {downloadingId === version.version_id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4" />
                      )}
                    </Button>
                    
                    {/* Archive/Restore Button */}
                    {version.is_archived ? (
                      canRestore && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-emerald-600 hover:text-emerald-700"
                          onClick={() => setRestoreDialog({ open: true, version })}
                          data-testid={`version-restore-${version.version_id}`}
                        >
                          <ArchiveRestore className="h-4 w-4" />
                        </Button>
                      )
                    ) : (
                      canArchive && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-amber-600 hover:text-amber-700"
                          onClick={() => setArchiveDialog({ open: true, version: { ...version, report_version_id: version.version_id } })}
                          data-testid={`version-archive-${version.version_id}`}
                        >
                          <Archive className="h-4 w-4" />
                        </Button>
                      )
                    )}
                    
                    {/* Hard Delete Button (Admin only - one-time cleanup) */}
                    {canHardDelete && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => setDeleteDialog({ open: true, version })}
                        data-testid={`version-delete-${version.version_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Archive Dialog */}
      <Dialog open={archiveDialog.open} onOpenChange={(open) => { 
        if (!open) { setArchiveDialog({ open: false, version: null }); setArchiveReason(''); }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive className="h-5 w-5 text-amber-600" />
              Archive Report
            </DialogTitle>
            <DialogDescription>
              This will hide the report from the default view and client portal. 
              The file will remain stored and can be restored by an administrator.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="archive-reason">Reason for archiving *</Label>
              <Textarea
                id="archive-reason"
                placeholder="Enter reason for archiving this report..."
                value={archiveReason}
                onChange={(e) => setArchiveReason(e.target.value)}
                className="min-h-[100px]"
                data-testid="archive-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setArchiveDialog({ open: false, version: null }); setArchiveReason(''); }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleArchive}
              disabled={archiving || !archiveReason.trim()}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="confirm-archive-btn"
            >
              {archiving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Archive className="h-4 w-4 mr-2" />}
              Archive Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Restore Dialog */}
      <Dialog open={restoreDialog.open} onOpenChange={(open) => { 
        if (!open) setRestoreDialog({ open: false, version: null });
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArchiveRestore className="h-5 w-5 text-emerald-600" />
              Restore Archived Report
            </DialogTitle>
            <DialogDescription>
              This will restore the report to the default view. 
              If the report was published, it will also become visible in the client portal again.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {restoreDialog.version && (
              <div className="bg-slate-50 p-3 rounded-lg text-sm">
                <p><strong>Version:</strong> {restoreDialog.version.version_number}</p>
                <p><strong>Filename:</strong> {restoreDialog.version.filename}</p>
                {restoreDialog.version.archive_reason && (
                  <p className="mt-2"><strong>Archive reason:</strong> {restoreDialog.version.archive_reason}</p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRestoreDialog({ open: false, version: null })}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRestore}
              disabled={restoring}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-restore-btn"
            >
              {restoring ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArchiveRestore className="h-4 w-4 mr-2" />}
              Restore Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Hard Delete Dialog (One-time cleanup) */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => { 
        if (!open) setDeleteDialog({ open: false, version: null });
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Permanently Delete Report
            </DialogTitle>
            <DialogDescription className="text-red-600 font-medium">
              WARNING: This action cannot be undone!
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="bg-red-50 border border-red-200 p-3 rounded-lg text-sm text-red-700">
              <p>This will permanently:</p>
              <ul className="list-disc list-inside mt-2">
                <li>Delete the file from storage (R2)</li>
                <li>Remove the database record</li>
                <li>This CANNOT be recovered</li>
              </ul>
            </div>
            {deleteDialog.version && (
              <div className="bg-slate-50 p-3 rounded-lg text-sm">
                <p><strong>Version:</strong> {deleteDialog.version.version_number}</p>
                <p><strong>Filename:</strong> {deleteDialog.version.filename}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialog({ open: false, version: null })}
            >
              Cancel
            </Button>
            <Button
              onClick={handleHardDelete}
              disabled={deleting}
              variant="destructive"
              data-testid="confirm-delete-btn"
            >
              {deleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Job Reports Dialog (Admin only - removes reports but keeps job in schedule) */}
      <Dialog open={deleteJobDialog.open} onOpenChange={(open) => { 
        if (!open) setDeleteJobDialog({ open: false, job: null });
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Delete All Reports
            </DialogTitle>
            <DialogDescription className="text-red-600 font-medium">
              WARNING: This action cannot be undone!
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="bg-red-50 border border-red-200 p-3 rounded-lg text-sm text-red-700">
              <p>This will permanently:</p>
              <ul className="list-disc list-inside mt-2">
                <li>Delete ALL reports and files from storage for this job</li>
                <li>Remove all report versions from the repository</li>
                <li>This CANNOT be recovered</li>
              </ul>
              <p className="mt-2 font-medium text-green-700">✓ The job will remain in the schedule</p>
            </div>
            {deleteJobDialog.job && (
              <div className="bg-slate-50 p-3 rounded-lg text-sm space-y-1">
                <p><strong>Client:</strong> {deleteJobDialog.job.client_name}</p>
                <p><strong>Audit Date:</strong> {formatDate(deleteJobDialog.job.audit_date)}</p>
                <p><strong>Reports:</strong> {deleteJobDialog.job.reports?.length || 0} report(s) will be deleted</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteJobDialog({ open: false, job: null })}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteJobReports}
              disabled={deletingJob}
              variant="destructive"
              data-testid="confirm-delete-job-btn"
            >
              {deletingJob ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete All Reports
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

          {/* Attachments & Evidence section ends */}
          </div>
        )}
        
        {/* Summary Stock Audit Reports Section */}
        {(activeTab === 'all' || activeTab === 'summary-reports') && (
          <div className="space-y-4" data-testid="summary-reports-section-content">
            {activeTab === 'all' && (
              <h2 className="text-base font-semibold text-slate-800 flex items-center gap-2 mt-4 pt-4 border-t">
                <FileText className="h-4 w-4" /> Summary Stock Audit Reports
              </h2>
            )}
          {/* Summary Reports Filters - Simplified */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-wrap items-end gap-4">
                {/* Scope Toggle - Beverage default, optimized for frequent beverage audits */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Scope</label>
                  <div className="flex border rounded-lg overflow-hidden">
                    <button
                      onClick={() => setSummaryFilters(prev => ({ ...prev, scope: 'BEVERAGE' }))}
                      className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                        summaryFilters.scope === 'BEVERAGE' 
                          ? 'bg-violet-600 text-white' 
                          : 'bg-white text-slate-600 hover:bg-slate-50'
                      }`}
                      data-testid="scope-beverage-btn"
                    >
                      <Wine className="h-4 w-4" />
                      Beverage
                    </button>
                    <button
                      onClick={() => setSummaryFilters(prev => ({ ...prev, scope: 'FOOD' }))}
                      className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors border-l ${
                        summaryFilters.scope === 'FOOD' 
                          ? 'bg-orange-600 text-white' 
                          : 'bg-white text-slate-600 hover:bg-slate-50'
                      }`}
                      data-testid="scope-food-btn"
                    >
                      <UtensilsCrossed className="h-4 w-4" />
                      Food
                    </button>
                    <button
                      onClick={() => setSummaryFilters(prev => ({ ...prev, scope: 'SHOP' }))}
                      className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors border-l ${
                        summaryFilters.scope === 'SHOP' 
                          ? 'bg-purple-600 text-white' 
                          : 'bg-white text-slate-600 hover:bg-slate-50'
                      }`}
                      data-testid="scope-shop-btn"
                    >
                      <UtensilsCrossed className="h-4 w-4" />
                      Shop
                    </button>
                    <button
                      onClick={() => setSummaryFilters(prev => ({ ...prev, scope: '' }))}
                      className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors border-l ${
                        !summaryFilters.scope 
                          ? 'bg-slate-600 text-white' 
                          : 'bg-white text-slate-600 hover:bg-slate-50'
                      }`}
                      data-testid="scope-all-btn"
                    >
                      All
                    </button>
                  </div>
                </div>
                
                {/* Status Filter */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Status</label>
                  <Select
                    value={summaryFilters.status || '__all__'}
                    onValueChange={(value) => setSummaryFilters(prev => ({ 
                      ...prev, 
                      status: value === '__all__' ? '' : value 
                    }))}
                  >
                    <SelectTrigger className="w-[140px]" data-testid="summary-status-filter">
                      <SelectValue placeholder="All statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All Statuses</SelectItem>
                      <SelectItem value="DRAFT">Draft</SelectItem>
                      <SelectItem value="PENDING_APPROVAL">Pending Approval</SelectItem>
                      <SelectItem value="PUBLISHED">Published</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Date Range */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Audit Date</label>
                  <div className="flex gap-2">
                    <Input
                      type="date"
                      value={summaryFilters.date_from}
                      max={summaryFilters.date_to || undefined}
                      onChange={(e) => setSummaryFilters(prev => {
                        // iter452 — mirror the clamp behaviour in the client
                        // reports filter above so the range never inverts.
                        const newFrom = e.target.value;
                        const shouldClampTo = newFrom && prev.date_to && newFrom > prev.date_to;
                        return { ...prev, date_from: newFrom, date_to: shouldClampTo ? newFrom : prev.date_to };
                      })}
                      className="w-[140px]"
                      data-testid="summary-date-from"
                    />
                    <span className="self-center text-slate-400">to</span>
                    <Input
                      type="date"
                      value={summaryFilters.date_to}
                      min={summaryFilters.date_from || undefined}
                      onChange={(e) => setSummaryFilters(prev => {
                        const newTo = e.target.value;
                        const shouldClampFrom = newTo && prev.date_from && newTo < prev.date_from;
                        return { ...prev, date_to: newTo, date_from: shouldClampFrom ? newTo : prev.date_from };
                      })}
                      className="w-[140px]"
                      data-testid="summary-date-to"
                    />
                  </div>
                  <div className="flex gap-1.5 flex-wrap mt-1">
                    {[
                      { label: 'Last Month', offset: -1 },
                      { label: 'This Month', offset: 0 },
                      { label: 'Next Month', offset: 1 },
                    ].map(({ label, offset }) => {
                      const d = new Date();
                      const from = new Date(d.getFullYear(), d.getMonth() + offset, 1);
                      const to = new Date(d.getFullYear(), d.getMonth() + offset + 1, 0);
                      return (
                        <Button
                          key={label}
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-6 text-[11px] px-2"
                          onClick={() => setSummaryFilters(prev => ({
                            ...prev,
                            date_from: formatLocalDate(from),
                            date_to: formatLocalDate(to),
                          }))}
                          data-testid={`summary-date-shortcut-${label.toLowerCase().replace(' ', '-')}`}
                        >
                          {label}
                        </Button>
                      );
                    })}
                  </div>
                </div>
                
                {/* Search */}
                <div className="space-y-2 flex-1 min-w-[200px]">
                  <label className="text-sm font-medium text-slate-700">Search</label>
                  <Input
                    placeholder="Client, site, or stock number..."
                    value={summaryFilters.search}
                    onChange={(e) => setSummaryFilters(prev => ({ ...prev, search: e.target.value }))}
                    data-testid="summary-search"
                  />
                </div>
                
                {/* Search Button */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700 invisible">Action</label>
                  <Button 
                    onClick={loadSummaryReports}
                    disabled={summaryLoading}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="summary-search-btn"
                  >
                    {summaryLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                    Search
                  </Button>
                </div>
                {/* Has-AP quick filter */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700 invisible">AP</label>
                  <button
                    type="button"
                    onClick={() => {
                      setSummaryFilters(prev => ({ ...prev, has_ap: !prev.has_ap }));
                    }}
                    className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md border transition-colors ${
                      summaryFilters.has_ap
                        ? 'bg-amber-600 border-amber-600 text-white'
                        : 'bg-white border-slate-300 text-slate-600 hover:bg-amber-50'
                    }`}
                    data-testid="summary-has-ap-filter"
                    title="Show only reports with Action Plans"
                  >
                    <FileWarning className="h-4 w-4" />
                    Has AP
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Summary Error */}
          {summaryError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2" data-testid="summary-error-alert">
              <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0" />
              <span className="text-sm text-red-700">{summaryError}</span>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={loadSummaryReports}
                className="ml-2 text-red-600 hover:text-red-800"
              >
                Retry
              </Button>
              <button onClick={() => setSummaryError(null)} className="ml-auto text-red-600 hover:text-red-800">
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
          
          {/* Results Summary */}
          <div className="text-sm text-slate-600">
            {summaryLoading ? 'Loading...' : summaryError ? '' : `${summaryReports.total} Summary Stock Audit Reports found`}
          </div>
          
          {/* Summary Reports Table */}
          <Card>
            <CardContent className="p-0">
              {summaryLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
                </div>
              ) : summaryError ? (
                // Error state - show retry button
                <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                  <AlertCircle className="h-12 w-12 text-red-300 mb-3" />
                  <p className="text-red-600">Unable to load reports</p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={loadSummaryReports}
                    className="mt-3"
                  >
                    Try Again
                  </Button>
                </div>
              ) : summaryReports.items.length === 0 ? (
                // Empty state - no error, just no data
                <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                  <FileText className="h-12 w-12 text-slate-300 mb-3" />
                  <p>No Summary Stock Audit Reports found</p>
                  <p className="text-xs text-slate-400 mt-1">Create reports from eligible Stock Audit jobs</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Stock Number</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Client / Site</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Scope</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Audit Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {summaryReports.items.map((report) => (
                        <tr key={report.id} className="hover:bg-slate-50" data-testid={`summary-row-${report.id}`}>
                          <td className="px-4 py-3">
                            <span className="font-mono font-medium">{report.stock_number}</span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-medium">{report.client_name}</div>
                            <div className="text-xs text-slate-500">{report.site_name}</div>
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant="outline" className={`text-xs ${
                              report.scope === 'FOOD' 
                                ? 'text-orange-600 border-orange-300 bg-orange-50' 
                                : 'text-purple-600 border-purple-300 bg-purple-50'
                            }`}>
                              {report.scope === 'FOOD' ? (
                                <>
                                  <UtensilsCrossed className="h-3 w-3 mr-1" />
                                  Food
                                </>
                              ) : (
                                <>
                                  <Wine className="h-3 w-3 mr-1" />
                                  Beverage
                                </>
                              )}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            {report.audit_date ? new Date(report.audit_date).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric'
                            }) : '-'}
                          </td>
                          <td className="px-4 py-3">
                            <Badge className={`text-xs ${
                              report.status === 'PUBLISHED'
                                ? 'bg-green-100 text-green-800 border-green-300'
                                : report.status === 'PENDING_APPROVAL'
                                  ? 'bg-orange-100 text-orange-800 border-orange-300'
                                  : 'bg-amber-100 text-amber-800 border-amber-300'
                            }`}>
                              {report.status === 'PUBLISHED' ? (
                                <>
                                  <CheckCircle2 className="h-3 w-3 mr-1" />
                                  Published
                                </>
                              ) : report.status === 'PENDING_APPROVAL' ? (
                                <>
                                  <Clock className="h-3 w-3 mr-1" />
                                  Pending Approval
                                </>
                              ) : (
                                <>
                                  <Clock className="h-3 w-3 mr-1" />
                                  Draft
                                </>
                              )}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-1 flex-wrap">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => navigate(
                                  report.status === 'PUBLISHED'
                                    ? `/summary-reports/${report.id}/view`
                                    : `/summary-reports/${report.id}`
                                )}
                                data-testid={`view-summary-${report.id}`}
                              >
                                {report.status === 'PUBLISHED' ? (
                                  <><Eye className="h-3 w-3 mr-1" /> View</>
                                ) : report.status === 'PENDING_APPROVAL' ? (
                                  <><Eye className="h-3 w-3 mr-1" /> Review</>
                                ) : (
                                  <><ExternalLink className="h-3 w-3 mr-1" /> Edit</>
                                )}
                              </Button>
                              {/* Item 8: AP shortcut in Audit Report repository */}
                              {report.ap_id ? (
                                <Button
                                  size="sm"
                                  variant={report.ap_status === 'draft' ? 'outline' : 'secondary'}
                                  onClick={() => navigate(`/summary-reports/${report.id}/action-plan`)}
                                  title={`Action Plan — ${report.ap_status || 'open'}`}
                                  data-testid={`ap-shortcut-${report.id}`}
                                  className={report.ap_status === 'draft' ? 'border-amber-300 text-amber-700' : ''}
                                >
                                  {report.ap_status === 'draft'
                                    ? <><Clock className="h-3 w-3 mr-1" /> AP Draft</>
                                    : <><FileText className="h-3 w-3 mr-1" /> View AP</>}
                                </Button>
                              ) : (report.ap_decision === 'issue' && report.status === 'PUBLISHED') ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => navigate(`/summary-reports/${report.id}/action-plan`)}
                                  title="Create Action Plan"
                                  data-testid={`ap-create-${report.id}`}
                                  className="border-blue-200 text-blue-600"
                                >
                                  <><FileText className="h-3 w-3 mr-1" /> Create AP</>
                                </Button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
          </div>
        )}

        {/* ── AP8: Action Plans archive tab ─────────────────────────────── */}
        {activeTab === 'action-plans' && (
          <div data-testid="action-plans-section">
            <Card className="shadow-sm">
              <CardHeader className="py-3 px-4 bg-amber-50 border-b border-amber-100">
                <CardTitle className="text-sm font-semibold text-amber-900 flex items-center gap-2">
                  <FileWarning className="h-4 w-4" /> Action Plans Archive
                  {apList.length > 0 && (
                    <span className="ml-2 bg-amber-200 text-amber-900 text-xs font-medium px-2 py-0.5 rounded-full">
                      {apTotal > apList.length ? `${apList.length} of ${apTotal}` : apList.length}
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              {/* Search / filter bar — mirrors Reports / Stocktakes tabs */}
              <div className="px-4 py-3 border-b bg-white flex flex-wrap gap-2 items-center">
                <input
                  type="text"
                  className="border rounded px-3 py-1.5 text-sm w-48 focus:outline-none focus:ring-1 focus:ring-amber-400"
                  placeholder="Search site or client…"
                  value={apSearchText}
                  onChange={e => setApSearchText(e.target.value)}
                  data-testid="ap-search-text"
                />
                <select
                  className="border rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-amber-400"
                  value={apSearchStatus}
                  onChange={e => setApSearchStatus(e.target.value)}
                  data-testid="ap-search-status"
                >
                  <option value="">All statuses</option>
                  <option value="draft">Draft</option>
                  <option value="open">Open</option>
                  <option value="partially_resolved">Partially resolved</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
                <select
                  className="border rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-amber-400"
                  value={apSearchScope}
                  onChange={e => setApSearchScope(e.target.value)}
                  data-testid="ap-search-scope"
                >
                  <option value="">All scopes</option>
                  <option value="BEVERAGE">Beverage</option>
                  <option value="FOOD">Food</option>
                  <option value="COMBINED">Combined</option>
                </select>
                {(apSearchText || apSearchStatus || apSearchScope) && (
                  <button
                    className="text-xs text-gray-500 hover:text-gray-700 underline"
                    onClick={() => { setApSearchText(''); setApSearchStatus(''); setApSearchScope(''); }}
                    data-testid="ap-search-clear"
                  >
                    Clear
                  </button>
                )}
              </div>
              <CardContent className="p-0">
                {apListLoading && (
                  <div className="flex items-center gap-2 p-6 text-slate-500 text-sm">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading Action Plans…
                  </div>
                )}
                {apListError && (
                  <div className="flex items-center gap-2 p-4 text-red-600 text-sm">
                    <AlertCircle className="h-4 w-4" /> {apListError}
                  </div>
                )}
                {!apListLoading && !apListError && apList.length === 0 && (
                  <div className="p-8 text-center text-slate-400 text-sm">
                    No Action Plans found for this organisation.
                  </div>
                )}
                {!apListLoading && apList.length > 0 && (() => {
                  const needle = apSearchText.toLowerCase();
                  const filtered = apList.filter(ap => {
                    if (apSearchStatus && ap.status !== apSearchStatus) return false;
                    if (apSearchScope  && (ap.scope || '').toUpperCase() !== apSearchScope) return false;
                    if (needle && !(
                      (ap.site_name   || '').toLowerCase().includes(needle) ||
                      (ap.client_name || '').toLowerCase().includes(needle) ||
                      (ap.stock_number|| '').toLowerCase().includes(needle)
                    )) return false;
                    return true;
                  });
                  return (
                  <div className="overflow-x-auto">
                    {filtered.length === 0 ? (
                      <div className="p-8 text-center text-slate-400 text-sm">No results match your filters.</div>
                    ) : (
                    <table className="w-full text-xs border-collapse" data-testid="ap-archive-table">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                          <th className="px-3 py-2 text-left font-semibold text-slate-600">Ref</th>
                          <th className="px-3 py-2 text-left font-semibold text-slate-600">Site</th>
                          <th className="px-3 py-2 text-left font-semibold text-slate-600">Client</th>
                          <th className="px-3 py-2 text-left font-semibold text-slate-600">Scope</th>
                          <th className="px-3 py-2 text-left font-semibold text-slate-600">Status</th>
                          <th className="px-3 py-2 text-left font-semibold text-slate-600">Audit Date</th>
                          <th className="px-3 py-2 text-right font-semibold text-slate-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((ap, idx) => {
                          const statusColors = {
                            draft: 'bg-amber-100 text-amber-800',
                            open: 'bg-blue-100 text-blue-800',
                            partially_resolved: 'bg-purple-100 text-purple-800',
                            resolved: 'bg-green-100 text-green-800',
                            closed: 'bg-slate-100 text-slate-700',
                          };
                          return (
                            <tr key={ap.id}
                              className={`border-b border-slate-100 hover:bg-amber-50/40 ${idx % 2 === 1 ? 'bg-slate-50/30' : ''}`}
                              data-testid={`ap-archive-row-${ap.id}`}
                            >
                              <td className="px-3 py-2 font-mono text-slate-600">
                                {ap.stock_number || `AP-${ap.id}`}
                              </td>
                              <td className="px-3 py-2 font-medium text-slate-800">{ap.site_name || '—'}</td>
                              <td className="px-3 py-2 text-slate-600">{ap.client_name || '—'}</td>
                              <td className="px-3 py-2 text-slate-600">{(ap.scope || '').toLowerCase()}</td>
                              <td className="px-3 py-2">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusColors[ap.status] || 'bg-slate-100 text-slate-700'}`}>
                                  {ap.status}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-slate-600">
                                {ap.audit_date ? new Date(ap.audit_date).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—'}
                              </td>
                              <td className="px-3 py-2">
                                <div className="flex items-center gap-1 justify-end flex-wrap">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-6 px-2 text-xs"
                                    onClick={() => navigate(`/action-plans/${ap.id}`)}
                                    data-testid={`ap-archive-view-${ap.id}`}
                                  >
                                    <Eye className="h-3 w-3 mr-1" /> View
                                  </Button>
                                  {ap.report_id && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-6 px-2 text-xs text-slate-500"
                                      disabled={!!apPdfLoading[ap.id]}
                                      onClick={async () => {
                                        setApPdfLoading(prev => ({ ...prev, [ap.id]: true }));
                                        try {
                                          const token = localStorage.getItem('access_token') || '';
                                          const resp = await fetch(
                                            `${process.env.REACT_APP_BACKEND_URL}/api/action-plans/${ap.id}/pdf`,
                                            { headers: { Authorization: `Bearer ${token}` } }
                                          );
                                          if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
                                          const blob = await resp.blob();
                                          const url  = URL.createObjectURL(blob);
                                          const a    = document.createElement('a');
                                          a.href     = url;
                                          a.download = `action-plan-${ap.stock_number || ap.id}.pdf`;
                                          a.click();
                                          URL.revokeObjectURL(url);
                                        } catch (e) {
                                          alert(`PDF download failed: ${e.message}`);
                                        } finally {
                                          setApPdfLoading(prev => ({ ...prev, [ap.id]: false }));
                                        }
                                      }}
                                      data-testid={`ap-archive-pdf-${ap.id}`}
                                    >
                                      {apPdfLoading[ap.id]
                                        ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                        : <Download className="h-3 w-3 mr-1" />}
                                      PDF
                                    </Button>
                                  )}
                                  {ap.report_id && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-6 px-2 text-xs text-slate-500"
                                      onClick={() => navigate(`/summary-reports/${ap.report_id}/action-plan?plan=${ap.id}`)}
                                      data-testid={`ap-archive-edit-${ap.id}`}
                                    >
                                      <ExternalLink className="h-3 w-3 mr-1" /> Edit
                                    </Button>
                                  )}
                                  {canDeleteAP && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-6 px-2 text-xs text-red-500 hover:text-red-700 hover:bg-red-50"
                                      onClick={() => setApDeleteDialog({ open: true, ap, reason: '' })}
                                      data-testid={`ap-archive-delete-${ap.id}`}
                                    >
                                      <Trash2 className="h-3 w-3 mr-1" /> Delete
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    )}
                  </div>
                  );
                })()}
                {/* Load more — shown when the total exceeds the currently fetched slice */}
                {!apListLoading && apList.length > 0 && apTotal > apList.length && (
                  <div className="px-4 py-3 border-t bg-white text-center">
                    <button
                      onClick={() => loadApList(apList.length)}
                      disabled={apListLoading}
                      className="px-4 py-1.5 text-sm border border-amber-300 text-amber-700 rounded hover:bg-amber-50 disabled:opacity-50"
                      data-testid="ap-load-more-btn"
                    >
                      Load more ({apList.length} of {apTotal} shown)
                    </button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* AP Delete Dialog (Admin / Senior Scheduler) — rendered outside tab blocks so it mounts on all tabs */}
      <Dialog open={apDeleteDialog.open} onOpenChange={(open) => {
        if (!open) setApDeleteDialog({ open: false, ap: null, reason: '' });
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Delete Action Plan
            </DialogTitle>
            <DialogDescription className="text-red-600 font-medium">
              This cannot be undone. Only plans without client-authored content can be deleted.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <div className="bg-amber-50 border border-amber-200 p-3 rounded text-sm text-amber-700">
              <p>Deleting AP for <strong>{apDeleteDialog.ap?.site_name || `AP-${apDeleteDialog.ap?.id}`}</strong>.</p>
              <p className="mt-1 text-xs">The linked Summary Report and audit records are NOT affected.</p>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Reason for deletion</label>
              <Textarea
                value={apDeleteDialog.reason}
                onChange={e => setApDeleteDialog(d => ({ ...d, reason: e.target.value }))}
                placeholder="e.g. Created in error, duplicate plan… (minimum 10 characters)"
                className="text-sm h-20"
                data-testid="ap-delete-reason-input"
              />
              {(apDeleteDialog.reason?.trim() || '').length > 0 && (apDeleteDialog.reason?.trim() || '').length < 10 && (
                <p className="text-xs text-red-500 mt-1">Reason must be at least 10 characters ({(apDeleteDialog.reason?.trim() || '').length}/10)</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApDeleteDialog({ open: false, ap: null, reason: '' })}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteAP}
              disabled={deletingAp || (apDeleteDialog.reason?.trim() || '').length < 10}
              variant="destructive"
              data-testid="ap-delete-confirm-btn"
            >
              {deletingAp ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete AP
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReportsRepositoryPage;
